'use client'

import React, { JSX, PropsWithChildren, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, ConditionalValue, Dialog, Flex, Portal } from '@chakra-ui/react'

import { Button, ButtonDanger, ButtonDangerText, ButtonWhite } from '../Buttons/Button'

import { LeidStyledBody, LeidStyledFooter, LeidStyledHeader, LeidStyledModal, LeidStyledOverlay } from './leidStyles'

type Props = {
  isOpen: boolean
  onClose: () => void
  header?: string | JSX.Element
  isCentered?: boolean
  size?: ConditionalValue<'sm' | 'md' | 'lg' | 'xl' | 'xs' | 'cover' | 'full' | undefined>
  scrollBehavior?: ConditionalValue<'outside' | 'inside' | undefined>

  shouldDisplayFooter?: boolean
  onConfirmClick?: () => void
  buttonConfirmText?: string
  isDanger?: boolean
}

const LeidModal: React.FC<PropsWithChildren<Props>> = ({
  children,
  isOpen,
  onClose,
  header,
  size,
  isCentered = true,

  shouldDisplayFooter = false,
  onConfirmClick,
  buttonConfirmText = 'proceed',
  isDanger = false,
  scrollBehavior = 'inside',
}) => {
  const { t } = useTranslation('common')

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'scroll'
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return () => {}
  }, [isOpen])

  const onOpenChange = (open: boolean) => {
    if (!open) {
      onClose()
    }
  }

  const onConfirmationClick = () => {
    if (onConfirmClick) {
      onConfirmClick()
    }
    onClose()
  }

  return (
    <Dialog.Root
      scrollBehavior={scrollBehavior}
      preventScroll={false}
      open={isOpen}
      onOpenChange={(e) => onOpenChange(e.open)}
      placement={isCentered ? 'center' : 'top'}
      size={size}
    >
      <Portal>
        <LeidStyledOverlay />
        <Dialog.Positioner>
          <LeidStyledModal>
            {header && <LeidStyledHeader>{header}</LeidStyledHeader>}
            <LeidStyledBody>{children}</LeidStyledBody>

            {shouldDisplayFooter && (
              <LeidStyledFooter>
                {isDanger ? (
                  <>
                    <Flex marginRight="20px" width="50%" justifyContent="center" alignItems="center">
                      <ButtonDangerText onClick={onClose}>{t('cancel')}</ButtonDangerText>
                    </Flex>
                    <Box width="50%">
                      <ButtonDanger onClick={onConfirmationClick}>{t(buttonConfirmText)}</ButtonDanger>
                    </Box>
                  </>
                ) : (
                  <>
                    <Box marginRight="20px" width="50%">
                      <ButtonWhite onClick={onClose}>{t('cancel')}</ButtonWhite>
                    </Box>
                    <Box width="50%">
                      <Button onClick={onConfirmationClick}>{t(buttonConfirmText)}</Button>
                    </Box>
                  </>
                )}
              </LeidStyledFooter>
            )}
          </LeidStyledModal>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}

export default LeidModal
